import { useTolgee, useTranslate } from '@tolgee/react';
import { Skeleton } from 'antd';
import classNames from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import useGetOrderPreferences from '../../../hooks/graphql/useGetOrderPrefernces';
import { useGetSpecialOrderProducts } from '../../../hooks/graphql/useGetSpecialOrderProducts';
import { useEffectOnce } from '../../../hooks/utils/useEffectOnce';
import { useSelection } from '../../../hooks/utils/useSelection';
import { isoStringToLocalDate } from '../../../utils/calendarUtility';
import { toggleItemInArray } from '../../../utils/commonUtility';
import CheckoutPage from '../checkout-page/CheckoutPage';
import FoodCard from '../food-card/FoodCard';
import OrderPreferences from '../order-preferences/OrderPreferences';
import SpecialOrderHeader from '../special-order-header/SpecialOrderHeader';
import SpecialOrderMealSelector from '../special-order-meal-selector/SpecialOrderMealSelector';
import './SpecialOrderSection.scss';

const SpecialOrderSection: React.FC = () => {
  const { getSpecialOrderProducts, specialOrderProducts, loading } =
    useGetSpecialOrderProducts();
  const [orderSectionVisible, toggleOrderSectionVisibility] = useState(true);
  const { getOrderPreferences, orderPreferences } = useGetOrderPreferences();
  const { getLanguage } = useTolgee();
  const language = getLanguage();
  const { t } = useTranslate();

  // State
  const [selectedProductType, setSelectedProductType] =
    useState<string>('Main');
  const [selectedDate, setSelectedDate] = useState<{
    date: string;
    day: string;
  } | null>(null);

  // Custom hooks for selections
  const mealTypeSelection = useSelection<string>([]);
  const allergySelection = useSelection<string>([]);

  // Fetch order preferences once
  useEffectOnce(() => {
    getOrderPreferences();
  });

  // Sync meal types with order preferences
  useEffect(() => {
    if (orderPreferences.length > 0) {
      const preferenceNames = orderPreferences.map(
        (preference: any) => preference.titleEn,
      );
      mealTypeSelection.setSelectedItems(preferenceNames);
    }
  }, [orderPreferences]);

  // Fetch products whenever date/productType/language changes
  useEffect(() => {
    if (!selectedDate?.date || !selectedDate?.day) {
      return;
    }

    getSpecialOrderProducts({
      variables: {
        weekDate: isoStringToLocalDate(selectedDate.date),
        day: selectedDate.day,
      },
      fetchPolicy: 'no-cache',
    });
  }, [selectedDate]);

  // Handlers
  const handleDateSelect = (dateObj: { date: string; day: string }) => {
    setSelectedDate(dateObj);
  };

  const handleProductTypeSelect = (productType: string) => {
    setSelectedProductType(productType);
  };

  const handleAllergiesChange = (allergyId: string) => {
    const updatedAllergies = toggleItemInArray(
      allergySelection.selectedItems,
      allergyId,
    );
    allergySelection.setSelectedItems(updatedAllergies);
  };

  const handleMealTypeSelect = (mealType: string) => {
    mealTypeSelection.toggleSelection(mealType);
  };

  // Filter products and adjust by language + allergies
  const filteredProducts = useMemo(() => {
    return specialOrderProducts
      .filter(
        (food: any) =>
          mealTypeSelection.selectedItems.includes(food.mealType) &&
          food.productType === selectedProductType,
      )
      .map((food: any) => ({
        ...food,
        allergicItems:
          allergySelection.selectedItems.length === 0
            ? []
            : food.allergicItems.filter((item: any) =>
                allergySelection.selectedItems.includes(item.name),
              ),
      }));
  }, [
    specialOrderProducts,
    mealTypeSelection.selectedItems,
    selectedProductType,
    allergySelection.selectedItems,
  ]);

  return (
    <div className='special-order-section-container'>
      {/* Header Section */}
      <SpecialOrderHeader
        onDateSelect={handleDateSelect}
        selectedDate={selectedDate}
        orderSectionVisible={orderSectionVisible}
        toggleOrderSectionVisibility={toggleOrderSectionVisibility}
      />

      {orderSectionVisible ? (
        <>
          {/* Meal Type and Allergies Selector */}
          <SpecialOrderMealSelector
            selectedMealType={selectedProductType as any}
            onSelect={handleProductTypeSelect}
            onAllergiesSelect={handleAllergiesChange}
            selectedAllergies={allergySelection.selectedItems}
          />

          {/* Order Preferences Section */}
          <OrderPreferences
            orderPreferences={orderPreferences}
            language={language as any}
            onMealTypeSelect={handleMealTypeSelect}
            selectedMealType={mealTypeSelection.selectedItems}
          />

          {/* Products Grid */}
          <div
            className={classNames('special-order-grid', {
              'no-data': !loading && filteredProducts.length === 0,
            })}
          >
            {!loading ? (
              filteredProducts?.length > 0 ? (
                filteredProducts.map((food: any) => (
                  <FoodCard
                    key={food.productId}
                    foodInfo={food}
                    selectedDate={selectedDate}
                  />
                ))
              ) : (
                <div>{t('No dish available')}</div>
              )
            ) : (
              Array.from({ length: 11 }, (_, index) => <Skeleton key={index} />)
            )}
          </div>
        </>
      ) : (
        <CheckoutPage selectedDate={selectedDate} />
      )}
    </div>
  );
};

export default SpecialOrderSection;
